import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePatients } from '@/hooks/usePatients';
import { Patient } from '@/types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  Search,
  Dog,
  Calendar,
  Scale,
  User,
  Stethoscope,
  Plus,
  Edit,
} from 'lucide-react';
import PatientDetailModal from '@/components/PatientDetailModal';
import PatientWithOwnerForm from '@/components/PatientWithOwnerForm';
import PatientForm from '@/components/PatientForm';

const speciesOptions = [
  { value: '', label: 'Todas las especies' },
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

export default function Patients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // Con qué pestaña se abre el detalle: por el nombre entra a los datos, por el
  // estetoscopio a la historia clínica.
  const [detailTab, setDetailTab] = useState<'info' | 'history'>('info');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

  const { data: patients, isLoading } = usePatients({
    search: search || undefined,
    species: selectedSpecies || undefined,
    only_active: true,
  });

  // Abrir automáticamente el modal si hay un id en la URL
  useEffect(() => {
    const patientId = searchParams.get('id');
    if (patientId && patients && patients.length > 0) {
      const patient = patients.find(p => p.id === parseInt(patientId));
      if (patient) {
        setDetailTab('info');
        setSelectedPatient(patient);
        // Limpiar el parámetro de la URL
        setSearchParams({});
      }
    }
  }, [searchParams, patients, setSearchParams]);

  const getSexLabel = (sex: string) => {
    switch (sex) {
      case 'male':
        return 'Macho';
      case 'female':
        return 'Hembra';
      default:
        return '-';
    }
  };

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return '-';
    const birth = new Date(birthDate);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      years--;
    }
    if (years < 1) {
      const months =
        (today.getFullYear() - birth.getFullYear()) * 12 +
        today.getMonth() -
        birth.getMonth();
      return `${months} meses`;
    }
    return `${years} años`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
          <p className="text-gray-600">Lista de mascotas registradas</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Paciente
        </Button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre o microchip..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            options={speciesOptions}
            value={selectedSpecies}
            onChange={(e) => setSelectedSpecies(e.target.value)}
          />
        </div>
      </div>

      {/* Lista de pacientes */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : patients && patients.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Paciente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Propietario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Especie / Raza
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Edad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Peso
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {patients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => { setDetailTab('info'); setSelectedPatient(patient); }}
                        className="flex items-center group text-left"
                      >
                        <div className="flex-shrink-0 h-10 w-10 bg-cream-100 rounded-full flex items-center justify-center group-hover:bg-cream-200 transition-colors">
                          <Dog className="h-5 w-5 text-cream-700" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors flex items-center gap-1.5">
                            {patient.name}
                            {patient.deceased && (
                              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-normal">
                                Falleció
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {getSexLabel(patient.sex)}
                          </div>
                        </div>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {patient.owner ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/owners/${patient.owner_id}`);
                          }}
                          className="flex items-center text-sm text-gray-900 hover:text-primary-600 transition-colors"
                        >
                          <User className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="hover:underline">
                            {patient.owner.first_name} {patient.owner.last_name}
                          </span>
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{patient.species}</div>
                      {patient.breed && (
                        <div className="text-sm text-gray-500">{patient.breed}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        {calculateAge(patient.birth_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {patient.weight ? (
                        <div className="flex items-center text-sm text-gray-900">
                          <Scale className="h-4 w-4 text-gray-400 mr-2" />
                          {patient.weight} kg
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setDetailTab('history'); setSelectedPatient(patient); }}
                          title="Historia Clínica"
                          className="text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                        >
                          <Stethoscope className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPatient(patient)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {search || selectedSpecies
              ? 'No se encontraron pacientes'
              : 'No hay pacientes registrados'}
          </div>
        )}
      </div>

      {/* Modal de detalle de paciente */}
      <PatientDetailModal
        patient={selectedPatient}
        isOpen={!!selectedPatient}
        onClose={() => setSelectedPatient(null)}
        initialTab={detailTab}
      />

      {/* Modal de nuevo paciente */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Nuevo Paciente"
        size="lg"
      >
        <PatientWithOwnerForm onSuccess={() => setIsModalOpen(false)} />
      </Modal>

      {/* Modal de editar paciente */}
      <Modal
        isOpen={!!editingPatient}
        onClose={() => setEditingPatient(null)}
        title={`Editar ${editingPatient?.name || 'Paciente'}`}
        size="lg"
      >
        {editingPatient && (
          <PatientForm
            patient={editingPatient}
            ownerId={editingPatient.owner_id}
            onSuccess={() => setEditingPatient(null)}
          />
        )}
      </Modal>
    </div>
  );
}
